// components/Posts/DeletePostButton.tsx
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Post } from "@/core/types/postTypes";
import { useDeletePost } from "./hooks/useDeletePost";

interface DeletePostButtonProps {
  post: Post;
  onPostDeleted?: (postId: string) => void;
  compact?: boolean;
}

export const DeletePostButton: React.FC<DeletePostButtonProps> = ({
  post,
  onPostDeleted,
  compact = false,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({
    top: 0,
    left: 0,
    transformOrigin: "top left",
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const { deletePost, isDeleting, error } = useDeletePost({
    onSuccess: (postId, response) => {
      console.log("Post deleted successfully:", response);
      setShowConfirmation(false);
      onPostDeleted?.(postId);
    },
    onError: (error) => {
      console.error("Failed to delete post:", error);
    },
  });

  // Calculate dialog position and animation origin
  useEffect(() => {
    if (showConfirmation && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Dialog dimensions (approximate)
      const dialogHeight = 280;
      const dialogWidth = 320;

      // Use viewport coordinates directly since the dialog is positioned fixed
      let top = buttonRect.bottom + 8;
      let left = buttonRect.left - 8;
      let transformOrigin = "top left";

      // Check if dialog would go off screen vertically
      const spaceBelow = windowHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      if (spaceBelow < dialogHeight && spaceAbove > dialogHeight) {
        // Not enough space below, but enough space above
        top = buttonRect.top - dialogHeight - 8;
        transformOrigin = "bottom left";
      }

      // Check if dialog would go off screen horizontally
      const spaceRight = windowWidth - buttonRect.left;
      const spaceLeft = buttonRect.right;
      
      if (spaceRight < dialogWidth && spaceLeft > dialogWidth) {
        // Not enough space to the right, but enough space to the left
        left = buttonRect.right - dialogWidth + 8;
        transformOrigin = transformOrigin.includes("bottom")
          ? "bottom right"
          : "top right";
      }

      // Ensure minimum distance from viewport edges
      const maxLeft = windowWidth - dialogWidth - 8;
      const minLeft = 8;
      const maxTop = windowHeight - dialogHeight - 8;
      const minTop = 8;
      
      left = Math.max(minLeft, Math.min(left, maxLeft));
      top = Math.max(minTop, Math.min(top, maxTop));

      setDialogPosition({ top, left, transformOrigin });
    }
  }, [showConfirmation]);

  // Close on escape key and outside clicks
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowConfirmation(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dialogRef.current &&
        !dialogRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowConfirmation(false);
      }
    };

    const handleScroll = () => {
      setShowConfirmation(false);
    };

    if (showConfirmation) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll, true);
      dialogRef.current?.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [showConfirmation]);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await deletePost(post.id);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmation(false);
  };

  const renderDialog = () => {
    if (!showConfirmation) return null;

    const dialog = (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-xs opacity-0 animate-fade-in z-40"
        />

        {/* Confirmation Dialog */}
        <div
          ref={dialogRef}
          className="fixed bg-white border border-red-200 rounded-xl shadow-2xl p-6 scale-95 opacity-0 animate-scale-in z-50"
          style={{
            top: dialogPosition.top,
            left: dialogPosition.left,
            minWidth: "320px",
            maxWidth: "400px",
            transformOrigin: dialogPosition.transformOrigin,
          }}
          role="dialog"
          aria-labelledby="delete-title"
          aria-describedby="delete-description"
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <div className="flex-1 min-w-0">
              <h3
                id="delete-title"
                className="font-semibold text-gray-900 mb-2"
              >
                Delete Post?
              </h3>

              <div
                id="delete-description"
                className="text-sm text-gray-600 mb-4"
              >
                <p className="mb-3">
                  This action cannot be undone. This will permanently:
                </p>
                <ul className="space-y-1 text-xs text-gray-500 ml-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full" />
                    Delete the database record
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full" />
                    Move images back to ready folder
                  </li>
                </ul>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-4 h-4 text-red-500 mt-0.5 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="text-red-700 text-xs font-medium">
                      {error}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </div>
                  ) : (
                    "Delete Post"
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors duration-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );

    return createPortal(dialog, document.body);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setShowConfirmation(true);
        }}
        className={`
          ${compact ? "p-2" : "p-2.5"}
          text-gray-400 hover:text-red-500 hover:bg-red-50
          rounded-lg transition-all duration-200 group
          focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-1
        `}
        title="Delete post"
        aria-label="Delete post"
      >
        <svg
          className={`${compact ? "w-4 h-4" : "w-5 h-5"} group-hover:scale-110 transition-transform duration-200`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>

      {renderDialog()}
    </>
  );
};
